import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useSwipeable } from 'react-swipeable'

// Components
import AnimateSlideText from '../animation/AnimateSlideText'
import FullWidthCarouselItem from '../FullWidthCarouselItem'
import CarouselNav from '../CarouselNav'
import Container from '../Container'
import Grid from '../Grid'
import GridItem from '../GridItem'
import ContainedWrap from '../ContainedWrap'
import RichText from '../RichText'
import ScrollSection from '../ScrollSection'
import Spacer from '../Spacer'

// Styles
import {
  FullWidthCarouselWrapper,
  FullWidthCarouselMain,
  FullWidthCarouselHeader,
  FullWidthCarouselContainer,
  FullWidthCarouselHeading,
  FullWidthCarouselTrack,
  FullWidthCarouselTitles,
  FullWidthCarouselTitle,
  FullWidthCarouselDetails,
  FullWidthCarouselDetail,
} from './index.style'
import { breakpoints } from '../../styles/vars/breakpoints.style'

const FullWidthCarousel = ({
  id,
  featured,
  heading,
  carouselItems,
  linkId,
  nextSlideLabel,
  prevSlideLabel,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [transitions, setTransitions] = useState(true)
  const [minHeight, setMinHeight] = useState(0)

  const itemCount = useRef(carouselItems.length)

  const transitionTimeout = useRef(null)
  const detailRef = useRef([])

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (window.innerWidth < breakpoints.tabletL) {
        nextSlide()
      }
    },
    onSwipedRight: () => {
      if (window.innerWidth < breakpoints.tabletL) {
        prevSlide()
      }
    },
  })

  const prevSlide = useCallback(() => {
    if (transitions) return
    setTransitions(true)
    setCurrentIndex(currentIndex =>
      currentIndex === 0 ? itemCount.current - 1 : currentIndex - 1
    )
  }, [transitions, itemCount])

  const nextSlide = useCallback(() => {
    if (transitions) return
    setTransitions(true)
    setCurrentIndex(currentIndex =>
      currentIndex === itemCount.current - 1 ? 0 : currentIndex + 1
    )
  }, [transitions, itemCount])

  const enableSlide = useCallback(() => {
    transitionTimeout.current = null
    setTransitions(false)
  }, [])

  useEffect(() => {
    transitionTimeout.current = setTimeout(enableSlide, 1200)

    return () => {
      if (transitionTimeout.current) clearTimeout(transitionTimeout.current)
    }
  }, [currentIndex, enableSlide])

  const enforceMinHeight = useCallback(() => {
    if (!detailRef.current) return
    let min = 0
    setTimeout(() => {
      detailRef.current.forEach(el => {
        min = el.offsetHeight > min ? el.offsetHeight : min
      })
      setMinHeight(min)
    }, 500)
  }, [detailRef])

  useEffect(() => {
    enforceMinHeight()
    window.addEventListener('resize', enforceMinHeight)

    return () => {
      window.removeEventListener('resize', enforceMinHeight)
    }
  }, [enforceMinHeight])

  return (
    <>
      <ScrollSection>
        <FullWidthCarouselWrapper id={linkId && linkId}>
          <Spacer size={[115, 115]} />
          <FullWidthCarouselMain {...handlers}>
            <Container>
              <FullWidthCarouselHeader>
                <ContainedWrap size={600} center={true}>
                  <FullWidthCarouselHeading>
                    <RichText
                      id={`${id}-heading`}
                      content={heading}
                      forceHeading
                      delay={0}
                      delayEm={true}
                    />
                  </FullWidthCarouselHeading>
                </ContainedWrap>
              </FullWidthCarouselHeader>

              <Spacer size={[20, 60]} />

              <FullWidthCarouselContainer>
                <FullWidthCarouselTrack>
                  {carouselItems &&
                    React.Children.toArray(
                      carouselItems.map((item, itemIndex) => {
                        return (
                          <FullWidthCarouselItem
                            {...item}
                            id={id + '-item-' + itemIndex}
                            index={itemIndex}
                            active={currentIndex === itemIndex}
                          />
                        )
                      })
                    )}
                </FullWidthCarouselTrack>
              </FullWidthCarouselContainer>
              <Spacer size={[20, 40]} />
              <Grid>
                <GridItem desk={3}>
                  <AnimateSlideText id={`${id}-titles`}>
                    <FullWidthCarouselTitles>
                      {carouselItems &&
                        React.Children.toArray(
                          carouselItems.map((item, itemIndex) => {
                            return (
                              <FullWidthCarouselTitle
                                key={`${id}-title-${itemIndex}`}
                                active={currentIndex === itemIndex}
                              >
                                {carouselItems.length > 1 ? (
                                  <>
                                    {`${itemIndex + 1}/${itemCount.current} — ${
                                      item.title
                                    }`}
                                  </>
                                ) : (
                                  item.title
                                )}
                              </FullWidthCarouselTitle>
                            )
                          })
                        )}
                    </FullWidthCarouselTitles>
                  </AnimateSlideText>
                </GridItem>
                <GridItem desk={7}>
                  <AnimateSlideText id={`${id}-details`}>
                    <FullWidthCarouselDetails minH={minHeight}>
                      {carouselItems &&
                        React.Children.toArray(
                          carouselItems.map((item, itemIndex) => {
                            return (
                              <FullWidthCarouselDetail
                                key={`${id}-details-${itemIndex}`}
                                active={currentIndex === itemIndex}
                                ref={el => (detailRef.current[itemIndex] = el)}
                              >
                                <RichText
                                  content={item.featuredCaption}
                                  id={`${id}-details-${itemIndex}`}
                                  useInView={false}
                                  isInView={true}
                                />
                              </FullWidthCarouselDetail>
                            )
                          })
                        )}
                    </FullWidthCarouselDetails>
                  </AnimateSlideText>
                </GridItem>
                {carouselItems.length > 1 && (
                  <GridItem desk={2}>
                    <CarouselNav
                      id={`${id}-nav-featured`}
                      align={`flex-end`}
                      prevEvent={prevSlide}
                      nextEvent={nextSlide}
                    />
                  </GridItem>
                )}
              </Grid>
            </Container>
          </FullWidthCarouselMain>
          <Spacer size={[79, 170]} />
        </FullWidthCarouselWrapper>
      </ScrollSection>
    </>
  )
}

FullWidthCarousel.propTypes = {
  id: PropTypes.string.isRequired,
  heading: PropTypes.object,
  carouselItems: PropTypes.array,
}

export default FullWidthCarousel
